import { useEffect, useState, memo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Brain } from 'lucide-react';
import { usePartDeltaStreaming } from '@sourcepilot/ui/framework/data/dag-sync/hooks';

import { ThinkingPartReadAPISchema } from '@sourcepilot/generated/chat/types/thinkingpart';
import { z } from 'zod';

interface ThinkingPartRendererProps {
  part: z.infer<typeof ThinkingPartReadAPISchema>;
  isStreaming: boolean;
}

/**
 * ThinkingPartRenderer — renders model reasoning tokens inline as prose.
 *
 * Styled subtly (gray italic) to distinguish from primary response text
 * but otherwise flows with the rest of the conversation. Subscribes to
 * delta streams for live updates while the part is still being produced.
 */
function ThinkingPartRendererComponent({ part, isStreaming }: ThinkingPartRendererProps) {
  const [content, setContent] = useState(part.content || '');

  const handleDelta = useCallback((delta: any) => {
    const data = delta?.delta?.delta_data;
    const type = delta?.delta?.delta_type;
    if (type === 'thinking_delta' && data?.content_delta) {
      setContent((prev: string) => prev + data.content_delta);
    }
  }, []);

  usePartDeltaStreaming(
    part.is_streaming ? (part.id || null) : null,
    0,
    handleDelta
  );

  useEffect(() => {
    setContent(part.content || '');
  }, [part.content]);

  return (
    <div className="flex flex-row flex-grow gap-2 p-[5px]">
      <Brain className="w-4 h-4 mt-1 flex-shrink-0 text-gray-400" />
      <div className="markdown-content text-gray-500 italic text-sm flex-grow">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
        {isStreaming && (
          <span className="inline-block w-2 h-4 ml-0.5 bg-gray-400 animate-pulse" />
        )}
      </div>
    </div>
  );
}

function areEqual(prev: ThinkingPartRendererProps, next: ThinkingPartRendererProps) {
  return (
    prev.part.id === next.part.id &&
    prev.isStreaming === next.isStreaming &&
    prev.part.content === next.part.content
  );
}

export const ThinkingPartRenderer = memo(ThinkingPartRendererComponent, areEqual);
